import {
  DEFAULT_LANGUAGE, KEYS, SUPPORTED_LENGTHS, maxGuessesFor, readJson, scopedKey, writeJson,
  type EvaluatedGuess, type GameMode, type GameState, type GameStatus, type Language,
  type StorageAdapter,
} from '@lexicon/core';
import { WEB_KEYS } from '../lib/storage';

/** XP breakdown and outcome for a finished game. Written once by `finalizeGame`. */
export interface GameResult {
  won: boolean;
  guessCount: number;
  xpAwarded: number;
  /** Award before streak bonus and hint penalty. */
  xpBase: number;
  /** Daily streak bonus included in the award; always 0 for challenges. */
  xpStreakBonus: number;
  /** XP actually lost to hints (never more than the award allows). */
  xpHintPenalty: number;
  /** Daily: streak after this game. Challenge: the untouched current streak. */
  streakAfter: number;
  finishedAt: number;
  /**
   * Flat bonus for finishing rung 7 (spec §10.3). Absent on every other
   * game — including journey games that did not complete a run — so old
   * sessions stay valid.
   */
  xpJourneyBonus?: number;
}

/** A persisted game: core's `GameState` plus its identity and result. */
export interface GameSession {
  /** `String(dayNumber)` for dailies, the challenge code for challenges. */
  id: string;
  mode: GameMode;
  answer: string;
  guesses: EvaluatedGuess[];
  status: GameStatus;
  hintsUsed: number;
  revealedHints: number[];
  result: GameResult | null;
  /**
   * Guessed words whose insight has been paid for this game (spec §3.7:
   * re-reading an already-revealed insight is free). Optional so sessions
   * written before this field existed still load correctly; `loadSession`
   * always returns an array (defaulting to `[]`) for callers.
   */
  revealedInsights?: string[];
}

/**
 * There is only ever one journey game, so — like the daily — it lives
 * under one key. The id is a constant rather than the rung so that boot
 * can find it without already knowing which rung was in play.
 */
export const JOURNEY_SESSION_ID = 'journey';

/**
 * Dailies and journeys each share one key; challenges get one key per code.
 *
 * The first two are language-scoped, because each language has its own
 * daily answer and its own journey run — an unscoped key would have one
 * language's in-progress board load onto the other's board and fail
 * `isSession`'s answer/length checks (or, worse, pass them). A challenge
 * is not: its code identifies a specific word, whatever language the
 * device happens to be set to.
 */
export function sessionKey(
  mode: GameMode,
  id: string,
  language: Language = DEFAULT_LANGUAGE,
): string {
  if (mode === 'daily') return scopedKey(KEYS.dailyGame, language);
  if (mode === 'journey') return scopedKey(WEB_KEYS.journeyGame, language);
  return WEB_KEYS.challengeGame(id);
}

export function toSession(
  id: string,
  state: GameState,
  result: GameResult | null,
  revealedInsights: readonly string[] = [],
): GameSession {
  return {
    id,
    mode: state.mode,
    answer: state.answer,
    guesses: state.guesses.map((g) => ({ word: g.word, tiles: [...g.tiles] })),
    status: state.status,
    hintsUsed: state.hintsUsed,
    revealedHints: [...state.revealedHints],
    result,
    revealedInsights: [...revealedInsights],
  };
}

export function toGameState(session: GameSession): GameState {
  return {
    answer: session.answer,
    mode: session.mode,
    guesses: session.guesses.map((g) => ({ word: g.word, tiles: [...g.tiles] })),
    status: session.status,
    hintsUsed: session.hintsUsed,
    revealedHints: [...session.revealedHints],
  };
}

const STATUSES: readonly string[] = ['playing', 'won', 'lost'];
const TILE_STATES: readonly string[] = ['correct', 'present', 'absent'];
/**
 * Built from SUPPORTED_LENGTHS rather than hardcoded so it cannot drift
 * from the length model (spec §10.6: stored sessions of any playable
 * length are valid, as long as they are internally consistent).
 */
const ANSWER_RE = new RegExp(
  `^[a-z]{${Math.min(...SUPPORTED_LENGTHS)},${Math.max(...SUPPORTED_LENGTHS)}}$`,
);

function isResult(v: unknown): v is GameResult {
  if (v === null || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  if (r.xpJourneyBonus !== undefined && typeof r.xpJourneyBonus !== 'number') return false;
  return typeof r.won === 'boolean'
    && typeof r.guessCount === 'number'
    && typeof r.xpAwarded === 'number'
    && typeof r.xpBase === 'number'
    && typeof r.xpStreakBonus === 'number'
    && typeof r.xpHintPenalty === 'number'
    && typeof r.streakAfter === 'number'
    && typeof r.finishedAt === 'number';
}

function isGuess(v: unknown, length: number): v is EvaluatedGuess {
  if (v === null || typeof v !== 'object') return false;
  const g = v as Record<string, unknown>;
  return typeof g.word === 'string'
    && g.word.length === length
    && Array.isArray(g.tiles)
    && g.tiles.length === length
    && g.tiles.every((t) => typeof t === 'string' && TILE_STATES.includes(t));
}

function isGuessShapedWord(v: unknown, length: number): v is string {
  return typeof v === 'string' && v.length === length && /^[a-z]+$/.test(v);
}

/** Structural guard — anything unexpected is treated as "no session" (spec §7). */
function isSession(v: unknown, mode: GameMode, id: string): boolean {
  if (v === null || typeof v !== 'object') return false;
  const s = v as Record<string, unknown>;
  if (s.id !== id || s.mode !== mode) return false;
  if (typeof s.answer !== 'string' || !ANSWER_RE.test(s.answer)) return false;
  // Everything below is measured against this one game's answer — its
  // length is the board's width and its row count (spec §10.1).
  const length = s.answer.length;
  const rows = maxGuessesFor(length);
  if (typeof s.status !== 'string' || !STATUSES.includes(s.status)) return false;
  if (typeof s.hintsUsed !== 'number' || !Number.isInteger(s.hintsUsed) || s.hintsUsed < 0) return false;
  if (
    !Array.isArray(s.revealedHints)
    || !s.revealedHints.every(
      (n) => typeof n === 'number' && Number.isInteger(n) && n >= 0 && n < length,
    )
  ) return false;
  if (
    !Array.isArray(s.guesses)
    || s.guesses.length > rows
    || !s.guesses.every((g) => isGuess(g, length))
  ) return false;
  // A won game always has a winning guess; a lost game always used every guess
  // (spec §3). Rejecting anything else here — not downstream — stops a
  // structurally-valid-but-impossible shape (e.g. status "won" with zero
  // guesses) from ever reaching dailyXp/challengeXp, where it would index
  // WIN_XP out of bounds and produce NaN.
  if (s.status === 'won' && s.guesses.length === 0) return false;
  if (s.status === 'lost' && s.guesses.length !== rows) return false;
  // Absent is fine (backward compatible with sessions written before this
  // field existed); present-but-malformed is corrupt like everything else
  // here — reject the whole session rather than risk silently dropping it.
  if (
    s.revealedInsights !== undefined
    && (!Array.isArray(s.revealedInsights) || !s.revealedInsights.every((w) => isGuessShapedWord(w, length)))
  ) return false;
  return s.result === null || s.result === undefined || isResult(s.result);
}

/** Load a session; null when missing, corrupt, or belonging to another id. */
export async function loadSession(
  storage: StorageAdapter,
  mode: GameMode,
  id: string,
  language: Language = DEFAULT_LANGUAGE,
): Promise<GameSession | null> {
  const raw = await readJson<unknown>(storage, sessionKey(mode, id, language), null);
  if (!isSession(raw, mode, id)) return null;
  const session = raw as GameSession;
  return { ...session, result: session.result ?? null, revealedInsights: session.revealedInsights ?? [] };
}

export async function saveSession(
  storage: StorageAdapter,
  session: GameSession,
  language: Language = DEFAULT_LANGUAGE,
): Promise<void> {
  await writeJson(storage, sessionKey(session.mode, session.id, language), session);
}

export async function clearSession(
  storage: StorageAdapter,
  mode: GameMode,
  id: string,
  language: Language = DEFAULT_LANGUAGE,
): Promise<void> {
  await storage.remove(sessionKey(mode, id, language));
}
